/**
 * Custom TableRow nodeView that renders rows as CSS grid blocks.
 *
 * When both `<table>` and `<tbody>` use `display: contents`, these grid rows
 * become direct flow participants in the pagination engine's float-based
 * page breaks — enabling true row-by-row table pagination instead of the
 * scrollable-tbody containment that tiptap-table-plus uses.
 *
 * Column widths are derived from the standard `colwidth` cell attribute
 * provided by @tiptap/extension-table.
 */
import type { NodeViewRendererProps } from '@tiptap/core';

function buildGridColumns(node: NodeViewRendererProps['node']): string {
  const cols: string[] = [];
  node.content.forEach((child) => {
    const colwidth: number[] | null | undefined = child.attrs.colwidth;
    const colspan: number = (child.attrs.colspan as number) ?? 1;
    if (Array.isArray(colwidth)) {
      for (const w of colwidth) {
        cols.push(w ? `${w}px` : '1fr');
      }
    } else {
      for (let i = 0; i < colspan; i++) {
        cols.push('1fr');
      }
    }
  });
  return cols.join(' ');
}

export function PaginatedTableRowView({ node }: NodeViewRendererProps) {
  const dom = document.createElement('tr');
  // Must use 'important' because PaginationPlus injects a <style> with:
  //   .rm-with-pagination table tbody > tr { display: table-row !important }
  // Normal inline styles lose to stylesheet !important; only setProperty
  // with the 'important' priority flag can override it.
  dom.style.setProperty('display', 'grid', 'important');
  dom.style.setProperty('grid-template-columns', buildGridColumns(node));
  dom.style.width = '100%';
  dom.style.position = 'relative';
  dom.style.boxSizing = 'border-box';

  return {
    dom,
    contentDOM: dom,
    update(updatedNode: NodeViewRendererProps['node']) {
      if (updatedNode.type.name !== 'tableRow') return false;
      dom.style.setProperty('grid-template-columns', buildGridColumns(updatedNode));
      return true;
    },
  };
}
